// Label + helper wrapper for API Security forms — passes label into AsecTextField (stacked style).
import React from "react";
import { Box, Stack, Typography } from "@mui/material";

import colors from "../../../../../../utils/colors";
import typography from "../../../../../../theme/typography";
import { asecHelperTextSx } from "../asecUi";
import { AsecTextField } from "./AsecTextField";

const asecFieldLabelSx = {
  ...typography.formField,
  fontWeight: 500,
  color: colors.neutral[800],
  mb: 1,
  display: "block",
};

function isStackedTextField(child) {
  return child?.type === AsecTextField || child?.type?.muiName === "TextField";
}

export function AsecField({ label, optHint, hint, helper, children, mb = 0, hideLabel = false, inline = false }) {
  const child = React.Children.only(children);
  const stacked = !inline && isStackedTextField(child);

  let fieldChild = child;
  if (stacked && label && !hideLabel && !child.props.label) {
    const labelText = typeof label === "string"
      ? `${label}${optHint ? ` ${optHint}` : ""}`
      : label;
    fieldChild = React.cloneElement(child, { label: labelText });
  }

  const showExternalLabel = label && !hideLabel && (inline || !stacked);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0, mb }}>
      {hideLabel ? (
        <Typography sx={{ ...asecFieldLabelSx, mb: 0, visibility: "hidden", userSelect: "none" }} aria-hidden="true">
          &nbsp;
        </Typography>
      ) : null}
      {showExternalLabel ? (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography sx={{ ...asecFieldLabelSx, mb: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
            {label}
            {optHint ? (
              <Typography component="span" sx={{ fontWeight: 400, color: colors.neutral[600], fontSize: 12 }}>
                {optHint}
              </Typography>
            ) : null}
          </Typography>
          {hint ? (
            <Typography sx={{ ml: "auto", fontSize: 12, color: colors.neutral[600], fontWeight: 400 }}>
              {hint}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
      {stacked && hint ? (
        <Typography sx={{ fontSize: 12, color: colors.neutral[600], mt: -0.25 }}>{hint}</Typography>
      ) : null}
      {fieldChild}
      {helper ? (
        <Typography sx={{ fontSize: 12, color: colors.neutral[600], ...asecHelperTextSx }}>{helper}</Typography>
      ) : null}
    </Box>
  );
}

export default AsecField;
